/**
 * LocalStorage Utility with SSR Guards for Next.js
 *
 * This utility provides a safe way to interact with localStorage
 * that works in both server and client environments.
 */

const isBrowser = typeof window !== "undefined";

export default class LocalStorage {
  static saveString(key: string, value: string): void {
    if (!isBrowser) return;
    localStorage.setItem(key, value);
  }

  static readString(key: string): string | null {
    if (!isBrowser) return null;
    return localStorage.getItem(key);
  }

  static saveObject(key: string, value: object): void {
    if (!isBrowser) return;
    const stringValue = JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  }

  static getObject<T>(key: string): T | null {
    if (!isBrowser) return null;
    const jsonValue = this.readString(key);

    if (jsonValue) return JSON.parse(jsonValue) as T;

    return null;
  }

  static saveObjectBase64(key: string, value: object): void {
    if (!isBrowser) return;
    const stringValue = JSON.stringify(value);
    this.saveString(key, window.btoa(stringValue));
  }

  static readObjectFromBase64<T>(key: string): T | null {
    if (!isBrowser) return null;
    const jsonValue = this.readString(key);

    if (jsonValue) {
      const jsonString = window.atob(jsonValue);
      return JSON.parse(jsonString) as T;
    }

    return null;
  }

  static delete(key: string): void {
    if (!isBrowser) return;
    localStorage.removeItem(key);
  }

  static clear(): void {
    if (!isBrowser) return;
    localStorage.clear();
  }

  static exists(key: string): boolean {
    if (!isBrowser) return false;
    return localStorage.getItem(key) !== null;
  }
}
